/**
 * Topbar: page title, queue logo, and notification bell.
 */
import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import notifyIcon from "../assets/img/icon-notification.svg";
import { NEUTRAL, ORANGE, PLUM, VIOLET } from "../styles/brandTokens";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import autotaskLogo from "../assets/img/autotask_logo.png";
import superopsLogo from "../assets/img/superops_ai_logo.jpg";
import { useUserMsp } from "../hooks/useUserMsp";
import { isAutotask, isSuperOps } from "../utils/mspUtils";
import { withOverlaySafeMenuProps } from "../utils/overlayMenu";
import ScheduleApi, { TheoNotification } from "../apis/scheduleApi";
import { useAppSelector } from "../hooks/storeHook";
import {
  nextNotificationPollDelay,
  NOTIFICATION_POLL_BASE_MS,
} from "../utils/notificationPollBackoff";

interface TopbarProps {
  pageTitle: string;
  notificationsEnabled?: boolean;
}

function getNotificationDotColor(notification: TheoNotification) {
  if (!notification.unread) return "transparent";
  if (notification.type === "schedule_succeeded") return "#2E7D32";
  if (notification.type === "schedule_failed") return "#D32F2F";
  return "#8062D9";
}

function notificationStatusLine(notification: TheoNotification) {
  if (notification.type === "billing_threshold") return "/billing";
  if (notification.entity_type === "schedule") {
    const status = String(notification.metadata_json?.status || "").trim();
    if (status) return status.replace(/_/g, " ");
  }
  return String(notification.body || "").trim();
}

function notificationTargetHref(notification: TheoNotification) {
  if (notification.type === "billing_threshold") return "/billing";
  if (notification.type === "billing_credit_request") return `/billing?mspId=${encodeURIComponent(String(notification.entity_id))}`;
  if (notification.entity_type === "schedule") {
    return `/schedules?scheduleId=${encodeURIComponent(String(notification.entity_id))}`;
  }
  return "";
}

const Topbar: React.FC<TopbarProps> = ({ pageTitle, notificationsEnabled = false }) => {
  const [notificationAnchor, setNotificationAnchor] = React.useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = React.useState<TheoNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const notificationsOpen = Boolean(notificationAnchor);
  const { userMspInfo } = useUserMsp();
  const scheduleApi = React.useMemo(() => new ScheduleApi(), []);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const pollDelayRef = React.useRef(NOTIFICATION_POLL_BASE_MS);
  const pollTimeoutRef = React.useRef<number | null>(null);

  const loadNotifications = React.useCallback(async (): Promise<boolean> => {
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      return true;
    }
    try {
      const response = await scheduleApi.listNotifications();
      setNotifications(Array.isArray(response.notifications) ? response.notifications : []);
      setUnreadCount(Number(response.unread_count || 0));
      pollDelayRef.current = NOTIFICATION_POLL_BASE_MS;
      return true;
    } catch {
      return false;
    }
  }, [scheduleApi, notificationsEnabled]);

  React.useEffect(() => {
    if (pollTimeoutRef.current != null) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (!notificationsEnabled) {
      setNotificationAnchor(null);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    let cancelled = false;
    pollDelayRef.current = NOTIFICATION_POLL_BASE_MS;

    const run = async () => {
      const ok = await loadNotifications();
      if (cancelled) return;
      pollDelayRef.current = nextNotificationPollDelay(pollDelayRef.current, ok);
      pollTimeoutRef.current = window.setTimeout(run, pollDelayRef.current);
    };

    void run();
    return () => {
      cancelled = true;
      if (pollTimeoutRef.current != null) {
        window.clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [accessToken, loadNotifications, notificationsEnabled]);

  const openNotifications = (event: React.MouseEvent<HTMLElement>) => {
    if (!notificationsEnabled) return;
    setNotificationAnchor(event.currentTarget);
    loadNotifications();
  };

  const closeNotifications = () => setNotificationAnchor(null);

  const handleNotificationClick = async (notification: TheoNotification) => {
    const targetHref = notificationTargetHref(notification);
    closeNotifications();
    if (targetHref) {
      window.open(targetHref, "_blank", "noopener,noreferrer");
    }
    try {
      if (notification.unread) {
        await scheduleApi.markNotificationRead(notification.id);
      }
    } catch {}
    loadNotifications();
  };

  const markAllRead = async () => {
    if (!notificationsEnabled) return;
    try {
      await scheduleApi.markAllNotificationsRead();
      await loadNotifications();
    } catch {}
  };

  const handleDeleteNotification = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notification: TheoNotification
  ) => {
    event.stopPropagation();
    if (!notificationsEnabled) return;

    setNotifications((current) => current.filter((item) => String(item.id) !== String(notification.id)));
    if (notification.unread) {
      setUnreadCount((current) => Math.max(0, current - 1));
    }

    try {
      await scheduleApi.deleteNotification(notification.id);
      await loadNotifications();
    } catch {
      await loadNotifications();
    }
  };

  const handleMarkNotificationRead = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notification: TheoNotification
  ) => {
    event.stopPropagation();
    if (!notificationsEnabled || !notification.unread) return;

    setNotifications((current) =>
      current.map((item) =>
        String(item.id) === String(notification.id)
          ? { ...item, unread: false, read_at: item.read_at || new Date().toISOString() }
          : item
      )
    );
    setUnreadCount((current) => Math.max(0, current - 1));

    try {
      await scheduleApi.markNotificationRead(notification.id);
      await loadNotifications();
    } catch {
      await loadNotifications();
    }
  };

  const handleDeleteAllNotifications = async () => {
    if (!notificationsEnabled || notifications.length === 0) return;

    setNotifications([]);
    setUnreadCount(0);

    try {
      await scheduleApi.deleteAllNotifications();
      await loadNotifications();
    } catch {
      await loadNotifications();
    }
  };

  // Get the appropriate logo for Queues
  const getQueueLogo = () => {
    if (pageTitle === "Queues") {
      if (userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type)) {
        return autotaskLogo;
      } else if (userMspInfo?.msp_type && isSuperOps(userMspInfo.msp_type)) {
        return superopsLogo;
      }
    }
    return null;
  };

  const queueLogo = getQueueLogo();

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: "rgba(255, 255, 255, 0.96)",
        color: NEUTRAL.text,
        boxShadow: "0 1px 2px rgba(36, 22, 61, 0.04)",
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backdropFilter: "blur(12px)",
        height: 72,
        justifyContent: "center",
      }}
    >
      <Toolbar
        sx={{
          minHeight: 72,
          padding: { xs: "0 16px", sm: "0 24px" },
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {queueLogo && (
            <img
              src={queueLogo}
              alt={`${pageTitle} logo`}
              style={{
                width: userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type) ? 90 : 42,
                height: userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type) ? 100 : 42,
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
          <Typography
            // variant="h6"
            fontWeight={700}
            fontFamily={"var(--theo-font-sans)"}
            sx={{
              color: NEUTRAL.text,
              fontSize: { xs: "1.125rem", sm: "1.25rem" },
              letterSpacing: "-0.015em",
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {notificationsEnabled ? (
            <>
              {/* Notification bell */}
              <Box sx={{
                position: 'relative',
                background: NEUTRAL.subtle,
                border: `1px solid ${NEUTRAL.border}`,
                borderRadius: "9px",
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                cursor: 'pointer',
                transition: "all 0.2s",
                "&:hover": { background: VIOLET.wash, borderColor: NEUTRAL.borderStrong },
              }}>
                <IconButton
                  size="large"
                  color="inherit"
                  sx={{ p: 0, background: 'none' }}
                  onClick={openNotifications}
                >
                  <Badge
                    badgeContent={unreadCount}
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: ORANGE.main,
                        color: PLUM.rail,
                        fontWeight: 700,
                        boxShadow: `0 0 0 2px ${NEUTRAL.surface}`,
                      },
                    }}
                  >
                    <img src={notifyIcon} alt="notify" style={{ width: 22, height: 22 }} />
                  </Badge>
                </IconButton>
              </Box>
              <Menu
                anchorEl={notificationAnchor}
                open={notificationsOpen}
                onClose={closeNotifications}
                {...withOverlaySafeMenuProps({
                  PaperProps: { sx: { width: 360, maxWidth: "calc(100vw - 24px)" } },
                })}
              >
                <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Notifications</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={markAllRead}
                      disabled={unreadCount === 0}
                      sx={{ textTransform: "none" }}
                    >
                      Mark all read
                    </Button>
                    <Tooltip title="Delete all notifications">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Delete all notifications"
                          disabled={notifications.length === 0}
                          onClick={handleDeleteAllNotifications}
                          sx={{
                            color: "#71717A",
                            "&:hover": {
                              color: "#D32F2F",
                              backgroundColor: "rgba(211, 47, 47, 0.08)",
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
                {notifications.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">No notifications</Typography>
                  </MenuItem>
                ) : (
                  notifications.slice(0, 8).map((notification) => {
                    const statusLine = notificationStatusLine(notification);
                    return (
                      <MenuItem
                        key={String(notification.id)}
                        onClick={() => handleNotificationClick(notification)}
                        sx={{ alignItems: "flex-start", whiteSpace: "normal", gap: 1, pr: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: getNotificationDotColor(notification),
                            mt: 0.8,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: 14 }}>
                            {notification.title}
                          </Typography>
                          {statusLine ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {statusLine}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0, mt: -0.25 }}>
                          <Tooltip title={notification.unread ? "Mark read" : "Already read"}>
                            <span
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <IconButton
                                size="small"
                                aria-label="Mark notification read"
                                disabled={!notification.unread}
                                onClick={(event) => handleMarkNotificationRead(event, notification)}
                                sx={{
                                  color: notification.unread ? "#2E7D32" : "#A1A1AA",
                                  "&:hover": {
                                    color: "#1B5E20",
                                    backgroundColor: "rgba(46, 125, 50, 0.08)",
                                  },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete notification">
                            <IconButton
                              size="small"
                              aria-label="Delete notification"
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => handleDeleteNotification(event, notification)}
                              sx={{
                                color: "#71717A",
                                "&:hover": {
                                  color: "#D32F2F",
                                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </Menu>
            </>
          ) : null}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
